import { z } from 'zod';
import { TYPED_CONFIRMATION_THRESHOLD } from '../constants/marketplaces.js';
import { inventorySearchSchema } from './inventory-filters.js';
import { kuralMarjSirasiniDenetle } from './pricing.js';

/**
 * §15.3.5 — Toplu işlemler.
 *
 * ZORUNLU AKIŞ:
 *   Seçim → Önizleme → Onay → batch_operations + snapshot → çalıştır
 *   → canlı ilerleme → sonuç raporu → [Geri Al]
 *
 * Hiçbir toplu işlem bu akışı atlayamaz. 1.000+ ürünü etkileyen ve geri alınamayan
 * işlemlerde ayrıca "ONAYLIYORUM" yazılması istenir.
 */

export const bulkOperationTypeSchema = z.enum([
  'SET_STATUS',
  'SET_QTY',
  'SET_HANDLING_TIME',
  'RESET_HANDLING_TIME',
  'TOGGLE_REPRICER',
  'ADD_TAG',
  'REMOVE_TAG',
  'APPLY_ASIN_RULE',
  'RECALCULATE_PRICE',
  'REQUOTE_SHIPPING',
  'ADD_TO_BLACKLIST',
  'DELETE',
]);
export type BulkOperationType = z.infer<typeof bulkOperationTypeSchema>;

/**
 * Hedef seçimi: ya açıkça seçilen kimlikler, ya da "filtreye uyan tüm ürünler".
 * §15.3.5 ikincisini özellikle istiyor: "Tümünü Seç (filtre sonucuna göre) — 50.000 satırı tek tıkla".
 */
export const bulkTargetSchema = z.union([
  z.object({ mode: z.literal('IDS'), ids: z.array(z.string()).min(1).max(100_000) }),
  z.object({ mode: z.literal('FILTER'), filter: inventorySearchSchema }),
]);
export type BulkTarget = z.infer<typeof bulkTargetSchema>;

/** Toplu marj alanı. Mesajlar ekranda alan adının yanına yazılıyor: "Min marj: en fazla %1.000 olabilir". */
const marjYuzdesi = () =>
  z.number().min(0, '0 ya da daha büyük olmalı').max(1000, 'en fazla %1.000 olabilir').optional();

export const bulkParamsSchema = z
  .object({
    /** SET_STATUS */
    status: z.enum(['ACTIVE', 'INACTIVE']).optional(),
    /** SET_QTY — sabit değer veya kaynak stoğa göre */
    qtyMode: z.enum(['FIXED', 'FROM_SOURCE']).optional(),
    qty: z
      .number()
      .int('tam sayı olmalı')
      .min(0, '0 ya da daha büyük olmalı')
      .max(9999, 'en fazla 9.999 olabilir')
      .optional(),
    /** SET_HANDLING_TIME */
    handlingTimeDays: z
      .number()
      .int('tam sayı olmalı')
      .min(0, '0 ya da daha büyük olmalı')
      .max(60, 'en fazla 60 gün olabilir')
      .optional(),
    /** TOGGLE_REPRICER */
    repricerEnabled: z.boolean().optional(),
    /** ADD_TAG / REMOVE_TAG */
    tagId: z.string().optional(),
    tagName: z.string().max(60, 'en fazla 60 karakter olabilir').optional(),
    /** APPLY_ASIN_RULE */
    minMarginPct: marjYuzdesi(),
    stdMarginPct: marjYuzdesi(),
    maxMarginPct: marjYuzdesi(),
    singleSellerMarginPct: marjYuzdesi(),
    /** ADD_TO_BLACKLIST */
    blacklistKind: z.enum(['BRAND', 'ASIN']).optional(),
    /** DELETE */
    deleteFromAmazon: z.boolean().optional(),
    alsoBlacklist: z.boolean().optional(),
  })
  .superRefine(kuralMarjSirasiniDenetle);
export type BulkParams = z.infer<typeof bulkParamsSchema>;

export const bulkPreviewSchema = z.object({
  type: bulkOperationTypeSchema,
  target: bulkTargetSchema,
  params: bulkParamsSchema.default({}),
});
export type BulkPreviewInput = z.infer<typeof bulkPreviewSchema>;

export const bulkExecuteSchema = bulkPreviewSchema.extend({
  /**
   * §15.3.5 — 1.000+ ürünü etkileyen ve geri alınamayan işlemlerde
   * kullanıcının "ONAYLIYORUM" yazması gerekir.
   */
  typedConfirmation: z.string().optional(),
});
export type BulkExecuteInput = z.infer<typeof bulkExecuteSchema>;

export const TYPED_CONFIRMATION_PHRASE = 'ONAYLIYORUM';

/** İşlem geri alınabilir mi? Amazon'dan silme geri alınamaz. */
export function isReversible(type: BulkOperationType, params: BulkParams): boolean {
  if (type === 'DELETE') return params.deleteFromAmazon !== true;
  if (type === 'REQUOTE_SHIPPING') return false; // dış servise iş açar
  return true;
}

/**
 * §15.3.5 — "ONAYLIYORUM yazma zorunluluğu sadece 1.000+ ürünü etkileyen ve
 * geri alınamayan işlemler için. Diğerlerinde tek tık onay yeterli."
 */
export function requiresTypedConfirmation(
  type: BulkOperationType,
  params: BulkParams,
  affectedCount: number,
): boolean {
  return !isReversible(type, params) && affectedCount >= TYPED_CONFIRMATION_THRESHOLD;
}

/**
 * Kullanıcının yazdığı onay ifadesi kabul edilir mi?
 *
 * Türkçe klavye tuzağı: kullanıcı küçük harfle "onaylıyorum" yazarsa `toUpperCase()`
 * bunu 'ONAYLIYORUM' yapar (JS 'ı' → 'I'), yani kabul edilir — istenen budur.
 * Boşluk kırpılır; başka hiçbir esneklik yoktur.
 */
export function isTypedConfirmationValid(input: string | undefined): boolean {
  return input?.trim().toUpperCase() === TYPED_CONFIRMATION_PHRASE;
}

/** §15.3.5 — Türkçe işlem adları. */
export const BULK_OPERATION_LABELS: Record<BulkOperationType, string> = {
  SET_STATUS: 'Aktif / Pasif yap',
  SET_QTY: 'Stok adedini değiştir',
  SET_HANDLING_TIME: 'Hazırlama süresini değiştir',
  RESET_HANDLING_TIME: 'Hazırlama süresini otomatiğe döndür',
  TOGGLE_REPRICER: "Repricer'ı aç / kapat",
  ADD_TAG: 'Etiket ekle',
  REMOVE_TAG: 'Etiket çıkar',
  APPLY_ASIN_RULE: 'ASIN kuralı uygula (toplu marj)',
  RECALCULATE_PRICE: 'Fiyatı yeniden hesapla',
  REQUOTE_SHIPPING: 'Kargo ücretini yeniden çek',
  ADD_TO_BLACKLIST: 'Yasaklı listeye ekle',
  DELETE: 'Sil',
};

// ── §13.7 ASIN / SKU ile ürün silme ─────────────────────────────────────────

export const identifierKindSchema = z.enum(['ASIN', 'SKU']);
export type IdentifierKind = z.infer<typeof identifierKindSchema>;

/** §13.7 — "Textarea'ya ASIN listesi (max 50.000) veya SKU listesi." */
export const MAX_IDENTIFIER_LINES = 50_000;

/**
 * Kısmi eşleşmede aynı anda kaç önek aranabilir.
 *
 * Her önek ayrı bir `ILIKE 'x%'` koşuluna dönüşür. 80.451 gerçek ürünle ölçüldü:
 * 50 önekte PostgreSQL trigram indeksinde kalıyor (~15 ms), 200 önekte plan
 * seq scan'e düşüyor (~1.035 ms). Sınırı aşan istek kabul edilirse 50.000 önekle
 * gelen bir liste sunucuyu dakikalarca kilitlerdi.
 */
export const MAX_PARTIAL_SKU_PREFIXES = 50;

export const resolveIdentifiersSchema = z.object({
  kind: identifierKindSchema,
  /** Satır satır yapıştırılan ham metin. Ayrıştırma sunucuda yapılır. */
  raw: z
    .string()
    .min(1)
    .max(MAX_IDENTIFIER_LINES * 40),
  /**
   * SKU'da kısmi eşleşme (SellerFlash gibi): `EASYWH` yazınca o önekle başlayan
   * tüm SKU'lar bulunur. ASIN'de anlamsız olduğu için yok sayılır.
   */
  partialSkuMatch: z.boolean().default(false),
});
export type ResolveIdentifiersInput = z.input<typeof resolveIdentifiersSchema>;

/**
 * Yapıştırılan metni tanımlayıcı listesine çevirir.
 *
 * Kullanıcı Excel'den, e-postadan veya başka bir yazılımdan kopyalayabilir; bu yüzden
 * satır sonu, virgül, noktalı virgül ve sekme ayırıcı sayılır. ASIN'de boşluk olamayacağı için
 * boşluk da ayırıcı; SKU'da boşluk meşru, satır içinde kalıyor. Yinelenenler atılır ve
 * sıra korunur — kullanıcı listesini kendi yazdığı sırada görsün.
 */
export function parseIdentifiers(raw: string, kind: IdentifierKind = 'SKU'): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const piece of raw.split(kind === 'ASIN' ? /[\s,;]+/ : /[\r\n,;\t]+/)) {
    const value = piece.trim();
    if (value === '') continue;
    const key = value.toUpperCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(value);
  }
  return out;
}
